import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Alert,
  Grid,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import api from '../services/api';

interface SousDemarcheFormProps {
  demarcheId: number;
  sousDemarche?: any;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const SousDemarcheForm: React.FC<SousDemarcheFormProps> = ({
  demarcheId,
  sousDemarche,
  onSuccess,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    statut: 'en_attente',
    date_limite: '',
    commentaires: '',
  });

  useEffect(() => {
    if (sousDemarche) {
      setFormData({
        titre: sousDemarche.titre || '',
        description: sousDemarche.description || '',
        statut: sousDemarche.statut || 'en_attente',
        date_limite: sousDemarche.date_limite ? sousDemarche.date_limite.split('T')[0] : '',
        commentaires: sousDemarche.commentaires || '',
      });
    }
  }, [sousDemarche]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.titre.trim()) {
      setError('Le titre est requis');
      setLoading(false);
      return;
    }

    try {
      if (sousDemarche) {
        // Modification
        await api.put(`/demarches/${demarcheId}/sous-demarches/${sousDemarche.id}`, formData);
      } else {
        // Création
        await api.post(`/demarches/${demarcheId}/sous-demarches`, formData);
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Titre */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Titre de la sous-démarche"
            name="titre"
            value={formData.titre}
            onChange={handleChange}
            placeholder="Ex: Photocopie CNI"
          />
        </Grid>

        {/* Description */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Détails de la sous-démarche..."
          />
        </Grid>

        {/* Statut */}
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Statut"
            name="statut"
            value={formData.statut}
            onChange={handleChange}
          >
            <MenuItem value="en_attente">En attente</MenuItem>
            <MenuItem value="en_cours">En cours</MenuItem>
            <MenuItem value="termine">Terminé</MenuItem>
            <MenuItem value="annule">Annulé</MenuItem>
          </TextField>
        </Grid>

        {/* Date limite */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="date"
            label="Date limite"
            name="date_limite"
            value={formData.date_limite}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            helperText="Date limite prévue (optionnel)"
          />
        </Grid>

        {/* Commentaires */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Commentaires / Notes"
            name="commentaires"
            value={formData.commentaires}
            onChange={handleChange}
          />
        </Grid>
      </Grid>

      {/* Actions */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={loading}>
            Annuler
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={loading}
        >
          {loading ? 'Enregistrement...' : (sousDemarche ? 'Modifier' : 'Créer')}
        </Button>
      </Box>
    </Box>
  );
};

export default SousDemarcheForm;
